"use client";

import {Badge} from "@/components/ui/badge";
import {Building2, CalendarCheck2, CalendarMinus, MapPinHouse, Shapes, SlidersHorizontal, X,} from "lucide-react";
import {computed, Signal} from "@preact/signals-react";
import {useRouter} from "next/navigation";
import {
  applyQueryToParams,
  categoryNames,
  cityNames,
  clearSelectFilters,
  locationNames,
  onSelectCategories,
  onSelectCities,
  onSelectLocations,
  searchText,
  selectedCategories,
  selectedCities,
  selectedEndDate,
  selectedLocations,
  selectedStartDate,
  setSearchText,
  setSelectedEndDate,
  setSelectedStartDate,
} from "../store/event-list.store";
import {ICityV2} from "@/app/dashboard/models/City";
import {ICategoryV2} from "@/app/dashboard/models/EventCategory";
import {ILocationV2} from "@/app/dashboard/models/Location";
import {useEffect} from "react";
import { Button } from "@/components/ui/button";

const removeItem = <T extends { id: string }>(
  items: T[],
  itemId: string,
  onSelect: (updatedItems: T[]) => void,
  routerPush: (href: string) => void
) => {
  const updated = items.filter((i) => i.id !== itemId);
  onSelect(updated);
  applyQueryToParams(routerPush);
};

const clearStartDate = (routerPush: (href: string) => void) => {
  setSelectedStartDate(null);
  applyQueryToParams(routerPush);
};

const clearEndDate = (routerPush: (href: string) => void) => {
  setSelectedEndDate(null);
  applyQueryToParams(routerPush);
};

const getFilterObjectsFromIds = (
  objectsIds: string | undefined,
  objectsArray: Signal<ILocationV2[]> | Signal<ICategoryV2[]> | Signal<ICityV2[]>
) => {
  if (!objectsIds) {
    return [];
  }
  const objectsIdsArray = objectsIds.includes(",") ? objectsIds.split(",") : [objectsIds];
  return objectsIdsArray
    .map((id) => objectsArray.value.find((obj) => obj.id === id))
    .filter(Boolean);
};

export const FilterBadges = ({ params, onAnyChangesInFilters }: { params: any, onAnyChangesInFilters:(isAnyChang:boolean)=> void }) => {
  const router = useRouter();

  const cityObjects = computed(() =>
    getFilterObjectsFromIds(params.cityIds, cityNames)
  );
  const locationObjects = computed(() =>
    getFilterObjectsFromIds(params.locationIds, locationNames)
  );
  const categoryObjects = computed(() =>
    getFilterObjectsFromIds(params.categoryIds, categoryNames)
  );

  const isAnythingFiltered =
    searchText.value ||
    selectedCities.value.length > 0 ||
    selectedLocations.value.length > 0 ||
    selectedCategories.value.length > 0 ||
    selectedStartDate.value ||
    selectedEndDate.value;


  useEffect(() => {
    onAnyChangesInFilters(!!isAnythingFiltered)
  }, [isAnythingFiltered]);



  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* Search */}
      {params.searchText && (
        <Badge variant="secondary" className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3">
          <SlidersHorizontal className="h-4 w-4 mr-1" />
          <span>Search: {params.searchText}</span>
          <X
            size={16}
            className="cursor-pointer ml-1 hover:stroke-cherry-red-9"
            onClick={() => {
              setSearchText("");
              applyQueryToParams(router.push);
            }}
          />
        </Badge>
      )}

      {/* Cities */}
      {cityObjects.value.map((city) => (
        <Badge
          key={city.id}
          variant="secondary"
          className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3"
        >
          <Building2 className="h-4 w-4 mr-1" />
          <span>{city.name}</span>
          <X
            size={16}
            className="cursor-pointer ml-1 hover:stroke-cherry-red-9"
            onClick={() =>
              removeItem(
                selectedCities.value,
                city.id,
                onSelectCities,
                router.push
              )
            }
          />
        </Badge>
      ))}

      {/* Locations */}
      {locationObjects.value.map((location) => (
        <Badge
          key={location.id}
          variant="secondary"
          className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3"
        >
          <MapPinHouse className="h-4 w-4 mr-1 hover:stroke-cherry-red-9" />
          <span>{location.name}</span>
          <X
            size={16}
            className="cursor-pointer ml-1"
            onClick={() =>
              removeItem(
                selectedLocations.value,
                location.id,
                onSelectLocations,
                router.push
              )
            }
          />
        </Badge>
      ))}

      {/* Categories */}
      {categoryObjects.value.map((category) => (
        <Badge
          key={category.id}
          variant="secondary"
          className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3"
        >
          <Shapes className="h-4 w-4 mr-1" />
          <span>{category.name}</span>
          <X
            size={16}
            className="cursor-pointer ml-1 hover:stroke-cherry-red-9"
            onClick={() =>
              removeItem(
                selectedCategories.value,
                category.id,
                onSelectCategories,
                router.push
              )
            }
          />
        </Badge>
      ))}

      {/* Start Date */}
      {params.startDate && (
        <Badge variant="secondary" className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3">
          <CalendarCheck2 className="h-4 w-4 mr-1" />
          <span>{new Date(params.startDate).toDateString()}</span>
          <X
            size={16}
            className="cursor-pointer ml-1 hover:stroke-cherry-red-9"
            onClick={() => clearStartDate(router.push)}
          />
        </Badge>
      )}

      {/* End Date */}
      {params.endDate && (
        <Badge variant="secondary" className="flex items-center gap-1 text-base py-1 px-2 bg-stone-neutral-3 hover:bg-stone-neutral-3">
          <CalendarMinus className="h-4 w-4 mr-1" />
          <span>{new Date(params.endDate).toDateString()}</span>
          <X
            size={16}
            className="cursor-pointer ml-1"
            onClick={() => clearEndDate(router.push)}
          />
        </Badge>
      )}

      {/* Clear All */}
      {isAnythingFiltered && (
        <Button
          variant="link"
          onClick={() => {
            clearSelectFilters(router.push);
          }}
        >
          Clear All
        </Button>
      )}
    </div>
  );
};
